import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { compoundingReasons } from '@/lib/compoundingReasons';
import {
  Search,
  Edit,
  Package,
  Save,
  X,
  Loader2,
  Info,
  TriangleAlert,
  FileSpreadsheet,
  History,
  ShieldAlert,
  Plus,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { toast } from '@/hooks/use-toast';
import { PaginationParams, Product } from '@/lib/types';
import { getProducts, updateClinicCompoundingReason, updateProductPotentialAllergies } from '@/services/products.service';
import { useAuth } from '@/contexts/AuthContext';
import { setClinicPrice } from '@/services/products.service';
import SmartPagination from '../ui/smart-pagination';
import { cn, formatToLocalDate } from '@/lib/utils';
import { permissionCheck } from '@/lib/permission-check';
import { useClinic } from '@/contexts/ClinicContext';
import BulkUpdateDialog from './BulkUpdateDialog';
import { PriceHistoryDrawer } from './PriceHistoryDrawer';
import { Skeleton } from '@/components/ui/skeleton';

const ClinicProducts = () => {
  const { user } = useAuth();
  const { clinic } = useClinic();
  const { isPractitioner, isAdmin, rolePermissions } = useClinic();
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    search: '',
    sortBy: 'description',
    sortOrder: 'ASC',
    totalItems: 0,
    totalPages: 0,
    itemsPerPage: 10,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [priceHistoryDrawer, setPriceHistoryDrawer] = useState<{
    open: boolean;
    productId: string;
    productDescription: string;
  }>({ open: false, productId: '', productDescription: '' });

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const response = await getProducts(clinic?.id, pagination);

      setProducts(response.products);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== pagination.search) {
        setPagination(prev => ({ ...prev, search: searchTerm.trim(), page: 1 }));
      }
    }, 800);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchProducts();
  }, [
    pagination.page,
    pagination.limit,
    pagination.sortBy,
    pagination.sortOrder,
    pagination.search,
    clinic?.id,
  ]);

  return (
    <div className='space-y-4 sm:space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>
            Product Catalog
          </h1>
          <p className='mt-1 text-sm text-slate-500'>
            Manage your clinic's product catalog and pricing
          </p>
        </div>
        {permissionCheck(
          'products:update',
          rolePermissions,
          isPractitioner || isAdmin
        ) && (
          <Button
            onClick={() => setBulkUpdateOpen(true)}
            className='rounded-lg bg-brand-teal font-medium text-white shadow-sm hover:bg-brand-teal/90'
          >
            <FileSpreadsheet className='h-4 w-4' />
            Bulk Update
          </Button>
        )}
      </div>

      {/* Search Row */}
      <div className='flex flex-row items-center gap-3'>
        <div className='relative max-w-sm flex-1'>
          <Search className='absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400' />
          <Input
            placeholder='Search by name or NDC...'
            className='rounded-lg border-slate-200 bg-white pl-9'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value.trimStart())}
          />
        </div>
      </div>

      {/* Product List Table */}
      <div className='overflow-hidden rounded-md border border-slate-200 bg-white'>
        {isLoading ? (
          <ProductTableSkeleton />
        ) : pagination.totalItems === 0 ? (
          <ProductEmptyState />
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[1150px] text-sm'>
              <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                <tr className='border-b border-slate-200'>
                  <th
                    className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    #
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Product List
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Compounding Reason
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Pharmacy Cost
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Patient Price
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Margin
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    DEA Schedule
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Updated
                  </th>
                  <th
                    className='px-4 py-3 text-center font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode='sync'>
                  {products.map((product, index) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      setProducts={setProducts}
                      index={index}
                      onViewHistory={() =>
                        setPriceHistoryDrawer({
                          open: true,
                          productId: product.id,
                          productDescription: product.description,
                        })
                      }
                    />
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className='border-t border-slate-200 px-2 py-3 sm:px-4'>
            <div className='flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-between'>
              <SmartPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={page => {
                  setPagination({ ...pagination, page });
                }}
                className='mx-0 justify-center sm:justify-start'
                maxVisiblePages={3}
              />
              <p className='whitespace-nowrap text-xs text-slate-500'>
                Showing <span className='font-medium text-slate-700'>{products.length}</span> of{' '}
                <span className='font-medium text-slate-700'>{pagination.totalItems}</span> products
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Update Dialog */}
      <BulkUpdateDialog
        open={bulkUpdateOpen}
        onOpenChange={setBulkUpdateOpen}
        currentSearch={pagination.search || ''}
        onSuccess={() => {
          fetchProducts();
        }}
      />

      <PriceHistoryDrawer
        open={priceHistoryDrawer.open}
        onClose={() =>
          setPriceHistoryDrawer({ open: false, productId: '', productDescription: '' })
        }
        productId={priceHistoryDrawer.productId}
        clinicId={clinic?.id || ''}
        productDescription={priceHistoryDrawer.productDescription}
      />
    </div>
  );
};

const ProductRow = ({
  product,
  setProducts,
  index,
  onViewHistory,
}: {
  product: Product;
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  index: number;
  onViewHistory: () => void;
}) => {
  const { user } = useAuth();
  const { clinic } = useClinic();
  const { isPractitioner, isAdmin, rolePermissions } = useClinic();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [price, setPrice] = useState(product.clinic_price_dollars);
  const [isReasonDialogOpen, setIsReasonDialogOpen] = useState(false);
  const [isSavingReason, setIsSavingReason] = useState(false);
  const [reasonValue, setReasonValue] = useState(
    product.clinic_reason_for_compounding_context || ''
  );
  const [isAllergyDialogOpen, setIsAllergyDialogOpen] = useState(false);
  const [isSavingAllergies, setIsSavingAllergies] = useState(false);
  const [allergyTags, setAllergyTags] = useState<string[]>(product.potential_allergies || []);
  const [newAllergyTag, setNewAllergyTag] = useState('');

  useEffect(() => {
    if (!isReasonDialogOpen) {
      setReasonValue(product.clinic_reason_for_compounding_context || '');
    }
  }, [product.clinic_reason_for_compounding_context, isReasonDialogOpen]);

  const calculateMargin = (clinicPrice: number, pharmacyPrice: number) => {
    const marginAmount = clinicPrice - pharmacyPrice;

    let marginPercentage = 0;
    if (pharmacyPrice === 0 && clinicPrice > 0) {
      marginPercentage = 100;
    } else if (pharmacyPrice === 0 && clinicPrice === 0) {
      marginPercentage = 0;
    } else if (pharmacyPrice > 0) {
      marginPercentage = (marginAmount / pharmacyPrice) * 100;
    }

    return {
      amount: marginAmount,
      percentage: marginPercentage,
    };
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      if (!clinic?.id) {
        console.error('User clinic data:', user?.clinic);
        throw new Error('Clinic ID not found. Please try logging in again.');
      }

      const response = await setClinicPrice(product.id, clinic.id, price);

      setProducts(prevProducts =>
        prevProducts.map(p => (p.id === response.id ? { ...p, ...response } : p))
      );

      toast({
        title: 'Price updated',
        description: 'Price updated successfully',
        variant: 'success',
      });
      setIsEditing(false);
    } catch (error: unknown) {
      console.error('Error updating price:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update price';
      toast({
        title: 'Error updating price',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveReason = async () => {
    try {
      setIsSavingReason(true);

      if (!clinic?.id) {
        throw new Error('Clinic ID not found. Please try logging in again.');
      }

      const trimmedReason = reasonValue.trim();
      const response = await updateClinicCompoundingReason(
        product.id,
        clinic.id,
        trimmedReason.length > 0 ? trimmedReason : null
      );
      const updatedProduct = response?.data?.data || response?.data || response;
      const fallbackActiveReason =
        updatedProduct?.clinic_reason_for_compounding_context
        || updatedProduct?.reason_for_compounding_context
        || null;

      setProducts(prevProducts =>
        prevProducts.map(p =>
          p.id === (updatedProduct?.id || product.id)
            ? { ...p, ...updatedProduct, active_reason_for_compounding_context: updatedProduct?.active_reason_for_compounding_context ?? fallbackActiveReason }
            : p
        )
      );

      toast({
        title: 'Compounding reason updated',
        description: 'Clinic override saved successfully',
        variant: 'success',
      });
      setIsReasonDialogOpen(false);
    } catch (error: unknown) {
      console.error('Error updating compounding reason:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update compounding reason';
      toast({
        title: 'Error updating reason',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSavingReason(false);
    }
  };

  const handleAddAllergyTag = () => {
    const tag = newAllergyTag.trim();
    if (tag && !allergyTags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      setAllergyTags([...allergyTags, tag]);
      setNewAllergyTag('');
    }
  };

  const handleRemoveAllergyTag = (tagToRemove: string) => {
    setAllergyTags(allergyTags.filter((t) => t !== tagToRemove));
  };

  const handleSaveAllergies = async () => {
    try {
      setIsSavingAllergies(true);
      await updateProductPotentialAllergies(product.id, allergyTags);
      setProducts((prev) =>
        prev.map((p) =>
          p.id === product.id ? { ...p, potential_allergies: allergyTags } : p
        )
      );
      toast({
        title: 'Potential allergies updated',
        description: 'Product allergy tags saved successfully',
        variant: 'success',
      });
      setIsAllergyDialogOpen(false);
    } catch (error: unknown) {
      console.error('Error updating potential allergies:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update potential allergies';
      toast({
        title: 'Error updating allergies',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSavingAllergies(false);
    }
  };

  const margin = calculateMargin(product.clinic_price_dollars, product.base_price_dollars);
  const hasPriceWarning =
    parseFloat(product.base_price_dollars as unknown as string) >=
    parseFloat(product.clinic_price_dollars as unknown as string);
  const isPriceEditable = parseFloat(product.base_price_dollars as unknown as string) > 0;
  const canUpdateProducts = permissionCheck(
    'products:update',
    rolePermissions,
    isPractitioner || isAdmin
  );

  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, delay: index * 0.03 }}
      className='border-b border-slate-100 transition-colors hover:bg-slate-50'
    >
      {/* # */}
      <td className='px-4 py-3.5 align-top text-sm text-slate-500'>
        #{String(index + 1).padStart(3, '0')}
      </td>

      {/* Product Info */}
      <td className='px-4 py-3.5 align-top'>
        <div className='flex items-start gap-3'>
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.description}
              className='h-9 w-9 flex-shrink-0 rounded-xl object-cover border border-slate-200'
            />
          ) : (
            <div className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-brand-copper/10'>
              <Package className='h-4 w-4 text-brand-copper' strokeWidth={2} />
            </div>
          )}
          <div className='min-w-0'>
            <h3 className='text-sm font-semibold leading-tight text-slate-900'>
              {product?.description || '-'}
            </h3>
            <p className='mt-0.5 text-xs text-slate-500'>NDC: {product?.ndc || '--'}</p>
            <Badge
              className={cn(
                'mt-1.5 px-2 py-0.5 text-[10px]',
                product.controlled_indicator
                  ? 'bg-red-100 text-red-700 hover:bg-red-100'
                  : 'bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/10'
              )}
            >
              {product.controlled_indicator ? 'Controlled' : 'Non-Controlled'}
            </Badge>
            {/* Potential Allergies */}
            {product.potential_allergies && product.potential_allergies.length > 0 && (
              <div className='mt-1.5 flex flex-wrap gap-1'>
                <ShieldAlert className='h-3 w-3 text-red-500 mt-0.5' />
                {product.potential_allergies.slice(0, 3).map((allergy, idx) => (
                  <Badge
                    key={idx}
                    className='px-1.5 py-0 text-[9px] bg-red-50 text-red-600 hover:bg-red-50 border-0'
                  >
                    {allergy}
                  </Badge>
                ))}
                {product.potential_allergies.length > 3 && (
                  <span className='text-[9px] text-red-500'>
                    +{product.potential_allergies.length - 3}
                  </span>
                )}
              </div>
            )}
            {canUpdateProducts && (
              <Button
                variant='ghost'
                size='sm'
                className='mt-1 h-5 px-1.5 text-[10px] text-slate-500 hover:text-red-600'
                onClick={() => {
                  setAllergyTags(product.potential_allergies || []);
                  setIsAllergyDialogOpen(true);
                }}
              >
                <ShieldAlert className='mr-0.5 h-3 w-3' />
                {product.potential_allergies?.length ? 'Edit Allergies' : 'Add Allergies'}
              </Button>
            )}
          </div>
        </div>

        {/* Potential Allergies Dialog */}
        <Dialog open={isAllergyDialogOpen} onOpenChange={setIsAllergyDialogOpen}>
          <DialogContent className='sm:max-w-[520px]'>
            <DialogHeader>
              <DialogTitle>Potential Allergies — {product.description}</DialogTitle>
            </DialogHeader>
            <div className='space-y-4'>
              <p className='text-xs text-slate-500'>
                Add allergy terms that this product could trigger. These will be matched against
                patient allergy profiles when prescribing.
              </p>
              <div className='flex gap-2'>
                <Input
                  placeholder='Type an allergy term...'
                  value={newAllergyTag}
                  onChange={(e) => setNewAllergyTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAllergyTag();
                    }
                  }}
                  className='text-sm'
                />
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleAddAllergyTag}
                  disabled={!newAllergyTag.trim()}
                >
                  <Plus className='h-4 w-4' />
                </Button>
              </div>
              <div className='min-h-[60px] rounded-md border border-slate-200 p-3'>
                {allergyTags.length === 0 ? (
                  <p className='text-xs text-slate-400 italic'>No allergy tags added</p>
                ) : (
                  <div className='flex flex-wrap gap-2'>
                    {allergyTags.map((tag, idx) => (
                      <Badge
                        key={idx}
                        className='gap-1 px-2.5 py-1 text-xs bg-red-50 text-red-700 hover:bg-red-100 border-0 cursor-default'
                      >
                        {tag}
                        <button
                          type='button'
                          onClick={() => handleRemoveAllergyTag(tag)}
                          className='ml-0.5 rounded-full p-0.5 hover:bg-red-200'
                        >
                          <X className='h-3 w-3' />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => {
                  setIsAllergyDialogOpen(false);
                  setAllergyTags(product.potential_allergies || []);
                }}
                disabled={isSavingAllergies}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveAllergies} disabled={isSavingAllergies}>
                {isSavingAllergies ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </td>

      {/* Compounding Reason */}
      <td className='px-4 py-3.5 align-top'>
        <div className='flex flex-col gap-2'>
          {product.active_reason_for_compounding_context ? (
            <p className='text-xs text-slate-700 max-w-[220px] whitespace-pre-wrap'>
              {product.active_reason_for_compounding_context}
            </p>
          ) : (
            <p className='text-xs text-slate-400 italic'>Not set</p>
          )}
          <Badge
            className={cn(
              'w-fit px-2 py-0.5 text-[10px]',
              product.clinic_reason_for_compounding_context
                ? 'bg-blue-50 text-blue-700 hover:bg-blue-50'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-100'
            )}
          >
            {product.clinic_reason_for_compounding_context ? 'Custom' : 'Default'}
          </Badge>
          <Button
            variant='outline'
            size='sm'
            className='h-7 text-xs w-fit'
            onClick={() => setIsReasonDialogOpen(true)}
            disabled={!canUpdateProducts}
          >
            <Edit className='mr-1 h-3 w-3' />
            Edit Reason
          </Button>
        </div>

        <Dialog open={isReasonDialogOpen} onOpenChange={setIsReasonDialogOpen}>
          <DialogContent className='sm:max-w-[520px]'>
            <DialogHeader>
              <DialogTitle>Edit Compounding Reason</DialogTitle>
            </DialogHeader>
            <div className='space-y-3'>
              <div>
                <p className='text-xs font-medium text-slate-600'>Default Reason (product/category)</p>
                <p className='mt-1 text-xs text-slate-500 whitespace-pre-wrap'>
                  {product.reason_for_compounding_context || 'Not set'}
                </p>
              </div>
              <div className='space-y-2'>
                <p className='text-xs font-medium text-slate-600'>Clinic Override</p>
                <Select
                  value={reasonValue || undefined}
                  onValueChange={(value) => setReasonValue(value === '__none__' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select a reason' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='__none__'>No reason</SelectItem>
                    {compoundingReasons.map(reason => (
                      <SelectItem key={reason} value={reason}>
                        {reason}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className='text-xs text-muted-foreground'>
                  Leave empty to use the default reason.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => {
                  setIsReasonDialogOpen(false);
                  setReasonValue(product.clinic_reason_for_compounding_context || '');
                }}
                disabled={isSavingReason}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveReason} disabled={isSavingReason}>
                {isSavingReason ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </td>

      {/* Pharmacy Cost */}
      <td className='px-4 py-3.5 align-top'>
        <div className='flex items-center gap-1'>
        <p className='text-sm font-medium text-slate-900'>
          $
          {isNaN(product.base_price_dollars)
            ? '0.00'
            : Number(product.base_price_dollars).toFixed(2)}
        </p>
        {!isPriceEditable && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <TriangleAlert className='h-4 w-4 cursor-help text-yellow-500' />
              </TooltipTrigger>
              <TooltipContent side='top' className='border-slate-900 bg-slate-900'>
                <p className='text-xs font-medium text-yellow-400'>
                  Pricing cannot be set until the pharmacy cost is configured by the super admin.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        </div>
      </td>

      {/* Patient Price */}
      <td className='px-4 py-3.5 align-top'>
        {isEditing ? (
          <div>
            <Input
              type='number'
              step='0.01'
              value={price}
              className='h-8 w-20 text-sm'
              onKeyDown={e => {
                if (!price) return;
                if (product.base_price_dollars >= parseFloat(price as unknown as string)) return;
                if (parseFloat(price as unknown as string) > 10000) return;
                if (e.key === 'Enter') return handleSave();
              }}
              onChange={e => {
                const input = e.target.value;
                const regex = /^\d*\.?\d{0,2}$/;
                if (input === '' || regex.test(input)) {
                  setPrice(input as any);
                }
              }}
              autoFocus
            />
            {!!price && parseFloat(price as unknown as string) > 10000 && (
              <span className='mt-1 inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] text-red-600'>
                <Info className='h-3 w-3' />
                Max $10,000
              </span>
            )}
            {product.base_price_dollars >= parseFloat(price as unknown as string) && (
              <span className='mt-1 inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] text-red-600'>
                <Info className='h-3 w-3' />
                Below cost
              </span>
            )}
          </div>
        ) : (
          <div className='flex items-center gap-1.5'>
            <p className='text-sm font-medium text-slate-900'>
              ${isNaN(price as unknown as number) ? '0.00' : Number(price).toFixed(2)}
            </p>
            {hasPriceWarning && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TriangleAlert className='h-4 w-4 cursor-help text-yellow-500' />
                  </TooltipTrigger>
                  <TooltipContent side='top' className='border-slate-900 bg-slate-900'>
                    <p className='text-xs font-medium text-yellow-400'>
                      Patient price cannot be less than or equal to pharmacy cost
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </td>

      {/* Margin */}
      <td className='px-4 py-3.5 align-top'>
        <p className='whitespace-nowrap text-sm font-medium text-slate-900'>
          ${margin.amount.toFixed(2)}
          <span className='ml-1 text-xs font-normal text-slate-400'>
            ({margin.percentage.toFixed(1)}%)
          </span>
        </p>
      </td>

      {/* DEA Schedule */}
      <td className='px-4 py-3.5 align-top'>
        <p className='text-sm text-slate-900'>{product?.dea_schedule_level || '-'}</p>
      </td>

      {/* Updated */}
      <td className='px-4 py-3.5 align-top'>
        <p className='text-sm text-slate-600'>
          {formatToLocalDate(product.updated_at, 'MM/DD/YYYY')}
        </p>
      </td>

      {/* Actions */}
      <td className='px-4 py-3.5 align-top'>
        <div className='flex items-center justify-center gap-1'>
          {!isEditing ? (
            <>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={onViewHistory}
                      className='h-8 w-8 p-0 text-slate-500 hover:bg-brand-teal/10 hover:text-brand-teal'
                    >
                      <History className='h-4 w-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='top' className='border-slate-900 bg-slate-900 text-white'>
                    <p className='text-xs'>View price history</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {permissionCheck(
                'products:update',
                rolePermissions,
                isPractitioner || isAdmin
              ) &&
                isPriceEditable && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => setIsEditing(true)}
                          className='h-8 w-8 p-0 text-slate-500 hover:bg-brand-teal/10 hover:text-brand-teal'
                        >
                          <Edit className='h-4 w-4' />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side='left'
                        className='border-slate-900 bg-slate-900 text-white'
                      >
                        <p className='text-xs'>Edit price</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
            </>
          ) : (
            <>
              {permissionCheck(
                'products:update',
                rolePermissions,
                isPractitioner || isAdmin
              ) && (
                <>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={handleSave}
                          disabled={
                            isSaving ||
                            product.base_price_dollars >= parseFloat(price as unknown as string) ||
                            !price ||
                            parseFloat(price as unknown as string) > 10000
                          }
                          className='h-8 w-8 p-0 text-brand-teal hover:bg-brand-teal/10 hover:text-brand-teal'
                        >
                          {isSaving ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            <Save className='h-4 w-4' />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side='left'
                        className='border-slate-900 bg-slate-900 text-white'
                      >
                        <p className='text-xs'>Save</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => {
                            setIsEditing(false);
                            setPrice(product.clinic_price_dollars);
                          }}
                          className='h-8 w-8 p-0 text-slate-500 hover:bg-red-50 hover:text-red-500'
                        >
                          <X className='h-4 w-4' />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side='left'
                        className='border-slate-900 bg-slate-900 text-white'
                      >
                        <p className='text-xs'>Cancel</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </>
          )}
        </div>
      </td>
    </motion.tr>
  );
};

const ProductTableSkeleton = () => {
  return (
    <div className='overflow-x-auto'>
      <table className='w-full min-w-[1150px] text-sm'>
        <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
          <tr className='border-b border-slate-200'>
            <th
              className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
              style={{ fontSize: '0.7rem' }}
            >
              #
            </th>
            <th
              className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
              style={{ fontSize: '0.7rem' }}
            >
              Product List
            </th>
            <th
              className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
              style={{ fontSize: '0.7rem' }}
            >
              Compounding Reason
            </th>
            <th
              className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
              style={{ fontSize: '0.7rem' }}
            >
              Pharmacy Cost
            </th>
            <th
              className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
              style={{ fontSize: '0.7rem' }}
            >
              Patient Price
            </th>
            <th
              className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
              style={{ fontSize: '0.7rem' }}
            >
              Margin
            </th>
            <th
              className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
              style={{ fontSize: '0.7rem' }}
            >
              DEA Schedule
            </th>
            <th
              className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
              style={{ fontSize: '0.7rem' }}
            >
              Updated
            </th>
            <th
              className='px-4 py-3 text-center font-semibold uppercase tracking-wider text-slate-700'
              style={{ fontSize: '0.7rem' }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {[...Array(8)].map((_, index) => (
            <tr key={index} className='border-b border-slate-100'>
              <td className='px-4 py-3.5 align-top'>
                <Skeleton className='h-4 w-10' />
              </td>
              <td className='px-4 py-3.5 align-top'>
                <div className='flex items-start gap-3'>
                  <Skeleton className='h-9 w-9 flex-shrink-0 rounded-lg' />
                  <div className='space-y-2'>
                    <Skeleton className='h-4 w-48' />
                    <Skeleton className='h-3 w-32' />
                    <Skeleton className='h-5 w-20 rounded-full' />
                  </div>
                </div>
              </td>
              <td className='px-4 py-3.5'>
                <Skeleton className='h-4 w-28' />
              </td>
              <td className='px-4 py-3.5'>
                <Skeleton className='h-4 w-14' />
              </td>
              <td className='px-4 py-3.5'>
                <Skeleton className='h-4 w-14' />
              </td>
              <td className='px-4 py-3.5'>
                <Skeleton className='h-4 w-20' />
              </td>
              <td className='px-4 py-3.5'>
                <Skeleton className='h-4 w-8' />
              </td>
              <td className='px-4 py-3.5'>
                <Skeleton className='h-4 w-16' />
              </td>
              <td className='px-4 py-3.5'>
                <div className='flex items-center justify-center gap-1'>
                  <Skeleton className='h-8 w-8 rounded' />
                  <Skeleton className='h-8 w-8 rounded' />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProductEmptyState = () => {
  return (
    <div className='px-4 py-12 text-center'>
      <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100'>
        <Package className='h-7 w-7 text-slate-300' strokeWidth={1.5} />
      </div>
      <p className='text-sm font-medium text-slate-900'>No products found</p>
      <p className='mt-1 text-xs text-slate-500'>
        Products will appear here once they are added to your catalog
      </p>
    </div>
  );
};

export default ClinicProducts;
